import { Component, ElementRef, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { AgendamentoEscolhido } from '../../models/solicitacao-servico.model';
import { AgendaMensalStore, DIAS_SEMANA, DiaCalendario } from '../../services/agenda-mensal.store';

export type { AgendamentoEscolhido } from '../../models/solicitacao-servico.model';

/**
 * Escolha de data e hora para a solicitação de serviço, sobre a **agenda do serviço** na API.
 *
 * O calendário — o mês exibido, os dias com horário livre, os limites e a navegação — é o
 * `AgendaMensalStore`, o mesmo do wizard da área do cidadão. Aqui ficam o `<dialog>` e a
 * escolha (dia e horário). Os nomes que o template usa continuam no componente, como
 * delegações ao store: a marcação e o CSS do modal não mudaram.
 *
 * A página do serviço não tem escolha de posto: a agenda lida é sempre a do endereço principal.
 *
 * O que a tela mostra é uma fotografia: a vaga só é garantida na conclusão, e um horário tomado
 * nesse meio-tempo volta como 409.
 */
@Component({
  selector: 'app-modal-agendamento',
  templateUrl: './modal-agendamento.component.html',
  styleUrls: ['./modal-agendamento.component.css'],
  imports: [LucideModule],
  providers: [AgendaMensalStore],
})
export class ModalAgendamentoComponent {
  private readonly agenda = inject(AgendaMensalStore);

  /** O serviço cuja agenda é lida. Nulo, o modal não busca nada. */
  readonly codServico = input<number | null>(null);

  readonly confirmado = output<AgendamentoEscolhido>();

  readonly diasSemana = DIAS_SEMANA;

  readonly dataSelecionada = signal('');
  readonly horarioSelecionado = signal('');

  readonly carregando = this.agenda.carregando;
  readonly falhou = this.agenda.falhou;
  readonly rotuloMes = this.agenda.rotuloMes;
  readonly diasDoMes = this.agenda.diasDoMes;
  readonly podeVoltarMes = this.agenda.podeVoltarMes;
  readonly podeAvancarMes = this.agenda.podeAvancarMes;
  readonly mesSemHorarios = this.agenda.mesSemHorarios;

  private readonly dialogo = viewChild<ElementRef<HTMLDialogElement>>('dialogo');

  readonly horariosDoDia = computed<readonly string[]>(() => this.agenda.horariosDe(this.dataSelecionada()));

  readonly dataFormatada = computed(() => {
    const iso = this.dataSelecionada();

    if (!iso) return '';

    const [ano, mes, dia] = iso.split('-');

    return `${dia}/${mes}/${ano}`;
  });

  /** Só confirma horário que a agenda ainda oferece no mês carregado. */
  readonly podeConfirmar = computed(
    () => !!this.dataSelecionada() && this.horariosDoDia().includes(this.horarioSelecionado()),
  );

  /**
   * Reabre já mostrando a escolha anterior, para a pessoa poder só ajustar o horário. Cada
   * abertura lê a agenda de novo: no mês da escolha, senão no primeiro que a agenda oferece —
   * ou, antes de a API dizer qual é, no do relógio.
   */
  abrir(escolha?: AgendamentoEscolhido | null): void {
    this.dataSelecionada.set(escolha?.data ?? '');
    this.horarioSelecionado.set(escolha?.horario ?? '');

    this.agenda.definirFonte(this.codServico());

    const inicio = escolha?.data || this.agenda.limites()?.minima;

    if (inicio) {
      this.agenda.irParaMesDe(inicio);
    } else {
      this.agenda.carregarMes();
    }

    this.dialogo()?.nativeElement.showModal();
  }

  fechar(): void {
    this.dialogo()?.nativeElement.close();
  }

  mesAnterior(): void {
    this.agenda.mesAnterior();
  }

  proximoMes(): void {
    this.agenda.proximoMes();
  }

  /** "Tentar novamente" da falha. */
  recarregar(): void {
    this.agenda.recarregar();
  }

  /** Trocar de dia zera o horário: a grade seguinte é de outra data. */
  selecionarDia(dia: DiaCalendario): void {
    if (dia.indisponivel || dia.dia === null) return;

    this.dataSelecionada.set(dia.iso);
    this.horarioSelecionado.set('');
  }

  selecionarHorario(horario: string): void {
    this.horarioSelecionado.set(horario);
  }

  confirmar(): void {
    if (!this.podeConfirmar()) return;

    this.confirmado.emit({
      data: this.dataSelecionada(),
      horario: this.horarioSelecionado(),
    });

    this.fechar();
  }
}
